import { useCallback, useEffect, useRef, useState } from "react";

import { useSendWebEvent } from "~/hooks";
import { useAppContext } from "~/providers/app";
import type { Scale, ScaleBoundUnit, ScaleModel } from "~/types/hardware";

export type ScaleTrialPhase = "connecting" | "waiting" | "stable" | "error";

/** How long the scale gets to produce what we wait for before we give up. */
export const SCALE_TRIAL_TIMEOUT_MS = 15_000;

type ScaleTrialOptions = {
  scale: Scale;
  /** Null when the wrapper never reported one; the stored pairing then applies. */
  model: ScaleModel | null;
  unit: ScaleBoundUnit;
  /** Null lets the wrapper use its per-unit default. */
  decimals: number | null;
  /** The skip path only needs the scale to answer; configuring precision needs an item weighed. */
  waitFor: "reading" | "stable";
};

/**
 * Opens the scale with trial values and follows what comes back, without
 * saving anything. Re-connects whenever the values change, so the slider in
 * the precision step previews the driver's own output.
 */
export const useScaleTrial = ({
  scale,
  model,
  unit,
  decimals,
  waitFor
}: ScaleTrialOptions) => {
  const {
    hardware: {
      scales: { weight: reading, error }
    }
  } = useAppContext();
  const sendWebEvent = useSendWebEvent();

  const [attempt, setAttempt] = useState(0);
  const [hasReading, setHasReading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const readingAtConnect = useRef(reading);
  const errorAtConnect = useRef(error);

  const scaleData = "model" in scale ? scale.scale : scale;
  const { deviceId, productId, vendorId } = scaleData;

  useEffect(() => {
    readingAtConnect.current = reading;
    errorAtConnect.current = error;
    setHasReading(false);
    setTimedOut(false);

    sendWebEvent({
      type: "SCALE_CONNECT_REQUEST",
      payload: {
        deviceId,
        productId,
        vendorId,
        ...(model !== null && { model }),
        unit,
        decimals
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the trial values and an explicit retry open the scale again; a reading or error arriving must not
  }, [
    deviceId,
    productId,
    vendorId,
    model,
    unit,
    decimals,
    attempt,
    sendWebEvent
  ]);

  useEffect(() => {
    return () => {
      sendWebEvent({ type: "SCALE_DISCONNECT_REQUEST" });
    };
  }, [sendWebEvent]);

  useEffect(() => {
    if (reading !== readingAtConnect.current) setHasReading(true);
  }, [reading]);

  const isStable = reading.status === "stable" && reading.weight > 0;
  const reached = waitFor === "reading" ? hasReading : hasReading && isStable;
  const failed = error !== null && error !== errorAtConnect.current;

  useEffect(() => {
    if (reached || failed) return;
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, SCALE_TRIAL_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [reached, failed, attempt]);

  const retry = useCallback(() => {
    sendWebEvent({ type: "SCALE_DISCONNECT_REQUEST" });
    setAttempt((count) => count + 1);
  }, [sendWebEvent]);

  let phase: ScaleTrialPhase = "connecting";
  if (failed || timedOut) phase = "error";
  else if (reached) phase = "stable";
  else if (hasReading) phase = "waiting";

  return {
    phase,
    /** The latest reading in the connection's format; only meaningful once `phase` is past connecting. */
    reading: hasReading ? reading : null,
    retry
  };
};
